/**
 * Async data hook
 *
 * A small data-fetching helper for read-only screens. It exists so the fetch
 * pattern — abort on unmount, ignore results from a stale request, keep the
 * previous value visible while reloading — is written once rather than
 * re-implemented on every screen.
 *
 * Why this file contains an eslint-disable
 * ----------------------------------------
 * React 19's `react-hooks/set-state-in-effect` rule flags calling setState
 * synchronously inside an effect, because it can cause a cascading render. That
 * concern is valid for derived state (which should be computed during render)
 * but not for network loading, where the UI genuinely must show a loading state
 * the moment the request starts. The alternative — deferring the flag into
 * microtasks — produces the same renders while hiding the intent.
 *
 * The suppression is confined to this one hook so there is a single place to
 * revisit if React ships an official data-fetching primitive.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '@/lib/api/client';

export interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  refreshing: boolean;
  reload: () => void;
}

export function useAsyncData<T>(
  key: string | null,
  fetcher: (signal: AbortSignal) => Promise<T>,
  fallbackMessage = 'Something went wrong. Please try again.',
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(key !== null);
  const [refreshing, setRefreshing] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const fetcherRef = useRef(fetcher);
  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (key === null) return;

    const controller = new AbortController();
    let cancelled = false;

    /* eslint-disable react-hooks/set-state-in-effect */
    setError(null);
    setRefreshing(true);
    setLoading(true);

    fetcherRef
      .current(controller.signal)
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled || controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : fallbackMessage);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setRefreshing(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [key, attempt, fallbackMessage]);

  return { data, error, loading, refreshing, reload };
}
